import { createSelector, createPropertySelectors, Selector } from '@ngxs/store';
import { LoadableStatus } from '@aiwyn/shared';
import { CxpDeliverableDetail, CxpStaffMember, CxpTask } from '../models';
import { CxpPermissions, DEFAULT_CXP_PERMISSIONS } from '../models/cxp-tasks-permissions.model';
import { CxpTasksState } from './cxp-tasks.state';
import { CxpTasksStateModel } from './cxp-tasks.model';

/**
 * Sort comparator: ascending by `dueDate` with `createdAt` as fallback.
 * Mixed task types are interleaved so the user sees a single chronological
 * stream rather than CRL-block / deliverable-block. Tasks with neither
 * date sink to the bottom.
 */
function byDueDateAsc(a: CxpTask, b: CxpTask): number {
  const dateA = a.dueDate ?? a.createdAt;
  const dateB = b.dueDate ?? b.createdAt;
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;
  return Date.parse(dateA) - Date.parse(dateB);
}

export class CxpTasksSelectors {
  static readonly slices = createPropertySelectors<CxpTasksStateModel>(CxpTasksState);

  @Selector([CxpTasksSelectors.slices.selectedRequestId])
  static getSelectedRequestId(selectedRequestId: number | null): number | null {
    return selectedRequestId;
  }

  @Selector([CxpTasksSelectors.slices.status])
  static isLoading(status: LoadableStatus): boolean {
    return status === LoadableStatus.LOADING;
  }

  @Selector([CxpTasksSelectors.slices.status])
  static isLoaded(status: LoadableStatus): boolean {
    return status === LoadableStatus.LOADED;
  }

  /**
   * The task list from `GET /cxp/tasks/list`, sorted for display. The backend
   * already joined `serviceLine` + `clientName` onto each row, so this selector
   * is a passthrough over the stored rows plus the shared sort — no client-side
   * join.
   */
  @Selector([CxpTasksSelectors.slices.tasks])
  static getTasks(
    // Default guards against rehydrated/partial state where the slice may be
    // undefined — selectors run during hydration before NGXS defaults are
    // applied and would otherwise throw "is not iterable".
    tasks: CxpTask[] = [],
  ): CxpTask[] {
    return [...tasks].sort(byDueDateAsc);
  }

  @Selector([CxpTasksSelectors.getTasks])
  static getTotalTaskCount(tasks: CxpTask[]): number {
    return tasks.length;
  }

  @Selector([CxpTasksSelectors.getTasks])
  static getOpenTaskCount(tasks: CxpTask[]): number {
    return tasks.filter((t) => t.status === 'open').length;
  }

  @Selector([CxpTasksSelectors.getTasks])
  static getUniqueStaffIds(tasks: CxpTask[]): number[] {
    return [...new Set(tasks.flatMap((task) => task.staffIds))];
  }

  @Selector([CxpTasksSelectors.slices.staff])
  static getStaff(staff: CxpStaffMember[] = []): CxpStaffMember[] {
    return staff;
  }

  /** Open-count for the every-page nav badge, from `GET /cxp/tasks/count`. */
  @Selector([CxpTasksSelectors.slices.badgeOpenCount])
  static getBadgeOpenCount(badgeOpenCount = 0): number {
    return badgeOpenCount;
  }

  /**
   * `canManageAccess` is not derivable from state — it depends on how the caller
   * authenticated — so it stays at its safe default here and
   * `CxpTasksPermissionsService` resolves the full object.
   */
  @Selector([CxpTasksSelectors.slices.status])
  static getPermissions(status: LoadableStatus): CxpPermissions {
    return {
      ...DEFAULT_CXP_PERMISSIONS,
      canViewTasks: status === LoadableStatus.LOADED,
    };
  }

  /**
   * Builds the partial deliverable-detail shape we already know from the
   * tasks list. Returns `null` when the deliverable isn't in the tasks slice
   * (e.g. direct URL load before tasks have loaded).
   *
   * Reads from `getTasks` (already carrying the joined service-line and client)
   * so this selector just picks the matching row.
   */
  static getDeliverableHydrationFromTasks(deliverableId: number) {
    return createSelector([CxpTasksSelectors.getTasks], (tasks: CxpTask[]): (Partial<CxpDeliverableDetail> & { id: number }) | null => {
      const task = tasks.find((t) => t.type === 'deliverable' && t.sourceId === deliverableId);
      if (!task) return null;

      return {
        id: task.sourceId,
        name: task.name,
        description: task.description,
        serviceLineName: task.serviceLine?.name ?? null,
        clientName: task.clientName,
      };
    });
  }
}
